import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FullPageStatus } from "@vv/ui";
import { requestDetailPath } from "@vv/types";
import {
  fetchRequests,
  RequestStatus,
  StatusChip,
  useRequestsDispatch,
  useRequestsSelector,
  type ValidationRequest,
} from "@vv/requests-frontend";
import {
  fetchMyMemberships,
  TeamRole,
  useTeamsDispatch,
  useTeamsSelector,
} from "@vv/teams-frontend";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export interface DashboardPageProps {
  currentUserId: string;
  isOwner: boolean;
  /** Global read-only privilege - sees every section system-wide, same bypass as Owner, but every action stays hidden (no TeamRole is ever granted just for holding this flag). */
  isViewer: boolean;
}

interface QueueSectionProps {
  title: string;
  requests: ValidationRequest[];
  emptyText: string;
}

function QueueSection({ title, requests, emptyText }: QueueSectionProps) {
  const navigate = useNavigate();

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {requests.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {requests.map((request) => (
              <Stack
                key={request.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ cursor: "pointer", py: 0.5 }}
                onClick={() => navigate(requestDetailPath(request.id))}
              >
                <Typography variant="body2">
                  {request.displayId} · {request.title}
                </Typography>
                <StatusChip status={request.status} />
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export function DashboardPage({ currentUserId, isOwner, isViewer }: DashboardPageProps) {
  const teamsDispatch = useTeamsDispatch();
  const requestsDispatch = useRequestsDispatch();

  const myMemberships = useTeamsSelector((state) => state.teams.myMemberships);
  const membershipsStatus = useTeamsSelector((state) => state.teams.myMembershipsStatus);
  const requests = useRequestsSelector((state) => state.requests.requests);
  const requestsStatus = useRequestsSelector((state) => state.requests.status);

  useEffect(() => {
    teamsDispatch(fetchMyMemberships());
    requestsDispatch(fetchRequests());
  }, [teamsDispatch, requestsDispatch]);

  const managerTeamIds = useMemo(
    () =>
      new Set(
        myMemberships
          .filter((member) => member.role === TeamRole.ValidationManager)
          .map((m) => m.teamId),
      ),
    [myMemberships],
  );
  const isRequestor =
    isOwner || isViewer || myMemberships.some((member) => member.role === TeamRole.Requestor);
  const isEngineer =
    isOwner ||
    isViewer ||
    myMemberships.some((member) => member.role === TeamRole.ValidationEngineer);
  const isManager = isOwner || isViewer || managerTeamIds.size > 0;

  const recentlyCompleted = requests.filter(
    (r) => r.requestorId === currentUserId && r.status === RequestStatus.Completed,
  );
  const pendingYourReview = requests.filter(
    (r) =>
      (r.status === RequestStatus.Submitted || r.status === RequestStatus.ReworkRequired) &&
      (isOwner || isViewer || managerTeamIds.has(r.teamId)),
  );
  const yourWorkQueue = requests.filter(
    (r) =>
      r.assignedEngineerIds.includes(currentUserId) &&
      [RequestStatus.Assigned, RequestStatus.InProgress].includes(r.status),
  );

  const loading =
    (membershipsStatus === "loading" || membershipsStatus === "idle") &&
    (requestsStatus === "loading" || requestsStatus === "idle") &&
    requests.length === 0 &&
    myMemberships.length === 0;

  if (loading) {
    return <FullPageStatus variant="loading" title="Loading your dashboard..." />;
  }

  const hasAnySection = isRequestor || isManager || isEngineer;

  if (!hasAnySection) {
    return (
      <FullPageStatus
        variant="empty"
        title="No role assigned yet"
        description="Once the Owner adds you to a validation team with a role, your queue will show up here."
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Dashboard
      </Typography>

      {isRequestor ? (
        <QueueSection
          title="Recently completed"
          requests={recentlyCompleted}
          emptyText="Nothing completed yet."
        />
      ) : null}

      {isManager ? (
        <QueueSection
          title="Pending your review"
          requests={pendingYourReview}
          emptyText="No submitted requests or rework claims waiting for review."
        />
      ) : null}

      {isEngineer ? (
        <QueueSection
          title="Your work queue"
          requests={yourWorkQueue}
          emptyText="Nothing assigned to you right now."
        />
      ) : null}
    </Box>
  );
}
